import { useMemo } from 'react';
import { addDays } from 'date-fns';
import { convertToSeconds } from 'utils/time';
import { scaleTime } from 'd3-scale';
import { useWindowSize } from 'react-use';
import {
  useMinWidthTabletLarge,
  useMinWidthMobileLarge,
  useMinWidthTablet,
} from 'hooks/useMediaQueries';

const useTimestamps = ({
  firstDayIndex,
  graphContainerRef,
  isMounted,
  midnightOfStartDate,
  lastDayIndex,
  additionalOffset = true,
}: {
  firstDayIndex: number;
  graphContainerRef: React.MutableRefObject<HTMLDivElement | null>;
  isMounted: boolean;
  midnightOfStartDate: Date;
  lastDayIndex: number;
  additionalOffset?: boolean;
}) => {
  const { width: windowWidth } = useWindowSize();

  const timestamps = useMemo(() => {
    const startDate = addDays(midnightOfStartDate, firstDayIndex);
    const endDate = addDays(midnightOfStartDate, lastDayIndex + 1);
    const startTimestamp = Math.floor(convertToSeconds(startDate.getTime()));
    const endTimestamp = Math.floor(convertToSeconds(endDate.getTime()));
    return {
      start: startTimestamp,
      end: endTimestamp,
    };
  }, [firstDayIndex, lastDayIndex, midnightOfStartDate]);

  const initialXPercentage = useMemo(() => {
    const currentUnixTimestamp = Math.floor(+new Date() / 1000);
    return (currentUnixTimestamp - timestamps.start) / (timestamps.end - timestamps.start);
  }, [timestamps.start, timestamps.end]);

  const isWidthMobileLargeMin = useMinWidthMobileLarge();
  const isWidthTabletMin = useMinWidthTablet();
  const isWidthTabletLgMin = useMinWidthTabletLarge();

  const additionalOffsetWidth = useMemo(() => {
    if (!additionalOffset) return 0;
    if (isWidthTabletLgMin) return 0;
    if (isWidthTabletMin) return 80;
    if (isWidthMobileLargeMin) return 60;
    return 40;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWidthTabletLgMin, isWidthMobileLargeMin, isWidthTabletMin]);

  const xScale = useMemo(() => {
    const width =
      (graphContainerRef?.current?.offsetWidth ?? 0) + additionalOffsetWidth || windowWidth;
    if (!!graphContainerRef?.current && isMounted) {
      return scaleTime().range([0, width]).domain([timestamps.start, timestamps.end]);
    }

    return null;
  }, [
    additionalOffsetWidth,
    graphContainerRef,
    isMounted,
    timestamps.end,
    timestamps.start,
    windowWidth,
  ]);

  return { initialXPercentage, timestamps, xScale };
};
export default useTimestamps;
